/**
 * Délai d'intention du méga-menu.
 *
 * Horizon ouvre le panneau dès que le curseur touche un élément de navigation.
 * Sur une barre dense, traverser « Homme · Femme · Combat » pour atteindre la
 * recherche fait donc clignoter trois panneaux. Le design system impose 120 ms
 * d'intention à l'ouverture et 200 ms de grâce à la fermeture.
 *
 * Ce fichier NE MODIFIE PAS le cœur d'Horizon. Le système d'événements du thème
 * résout `instance[method]` au moment de l'événement (voir assets/component.js) :
 * il suffit donc de remplacer les méthodes sur l'instance. Un futur `theme update`
 * n'entrera pas en conflit.
 *
 * Le clavier n'attend jamais : ouvrir au focus est une intention explicite,
 * la retarder donnerait une impression de lenteur au lecteur d'écran.
 */
/**
 * `morph.js` resynchronise les attributs depuis le HTML serveur et efface donc
 * tout marqueur posé en dataset. On mémorise les instances déjà greffées dans
 * un WeakSet : sinon une seconde greffe emballerait le wrapper dans un autre
 * wrapper et le délai doublerait silencieusement à 240 ms.
 */
const grafted = new WeakSet();

const OPEN_INTENT_MS = 120;
const CLOSE_GRACE_MS = 200;

/** @param {any} menu */
function enhance(menu) {
  if (grafted.has(menu) || typeof menu.activate !== 'function') return;
  grafted.add(menu);

  const openNow = menu.activate.bind(menu);
  const closeNow = menu.deactivate.bind(menu);

  let openTimer;
  let closeTimer;

  menu.activate = (event) => {
    // Revenir sur le menu pendant le délai de grâce annule la fermeture.
    clearTimeout(closeTimer);

    if (event?.type !== 'pointerenter') {
      clearTimeout(openTimer);
      openNow(event);
      return;
    }

    clearTimeout(openTimer);
    openTimer = setTimeout(() => openNow(event), OPEN_INTENT_MS);
  };

  menu.deactivate = (event) => {
    // Quitter avant la fin du délai annule l'ouverture : aucun panneau ne s'affiche.
    clearTimeout(openTimer);

    if (event?.type !== 'pointerleave') {
      closeNow(event);
      return;
    }

    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => closeNow(event), CLOSE_GRACE_MS);
  };
}

function enhanceAll(root = document) {
  const menus = root.querySelectorAll?.('header-menu');
  if (!menus) return 0;
  let pending = 0;
  for (const menu of menus) {
    // `activate` est une propriété d'instance posée à l'upgrade du custom
    // element. Si l'upgrade n'a pas encore eu lieu, on recomptera au tour
    // suivant plutôt que d'abandonner silencieusement.
    if (typeof menu.activate !== 'function') pending += 1;
    else enhance(menu);
  }
  return pending;
}

/**
 * L'upgrade des custom elements n'est pas garanti au moment où ce module
 * s'exécute : `whenDefined` signale l'enregistrement de la classe, pas la mise
 * à niveau des éléments déjà présents dans le document. On retente donc sur
 * quelques images avant d'abandonner — c'était la cause d'une greffe muette.
 */
function enhanceWithRetry(attempt = 0) {
  const pending = enhanceAll();
  if (pending > 0 && attempt < 20) {
    requestAnimationFrame(() => enhanceWithRetry(attempt + 1));
  }
}

customElements.whenDefined('header-menu').then(() => {
  enhanceWithRetry();

  // Le header peut être re-rendu par le theme editor ou la Section Rendering API.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches?.('header-menu') || node.querySelector?.('header-menu')) {
          enhanceWithRetry();
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
});
